import Organization from "./Organization";
import {ResourceOwnerType} from "./ResourceOwnerType";

export type AiAttributeResultType = "VALUE_RATING" | "VALUE_TEXT" | "VALUE_TEXTS" | "VALUE_SUMMARY";

export const AI_ATTRIBUTE_RESULT_TYPES: AiAttributeResultType[] = [
    "VALUE_RATING",
    "VALUE_TEXT",
    "VALUE_TEXTS",
    "VALUE_SUMMARY"
];

export type AiAttributeValueRenderType = "TEXT" | "CVE" | "URL";

export const AI_ATTRIBUTE_VALUE_RENDER_TYPES: AiAttributeValueRenderType[] = [
    "TEXT",
    "CVE",
    "URL"
];

export function formatAiAttributeResultType(resultType?: AiAttributeResultType | string): string {
    switch (resultType) {
        case "VALUE_RATING":
            return "rating (0-1)";
        case "VALUE_TEXT":
            return "text";
        case "VALUE_TEXTS":
            return "multiple texts";
        case "VALUE_SUMMARY":
            return "summary";
        default:
            return resultType ?? "-";
    }
}

export function describeAiAttributeResultType(resultType?: AiAttributeResultType | string): string {
    switch (resultType) {
        case "VALUE_RATING":
            return "Extract a numeric score between 0 and 1, usually a probability.";
        case "VALUE_TEXT":
            return "Extract one text value.";
        case "VALUE_TEXTS":
            return "Extract a list of text values.";
        case "VALUE_SUMMARY":
            return "Extract a longer free-form summary.";
        default:
            return "";
    }
}

export function getAiAttributeResultTypeOptions(): { value: AiAttributeResultType; label: string }[] {
    return AI_ATTRIBUTE_RESULT_TYPES.map(value => ({
        value,
        label: formatAiAttributeResultType(value)
    }));
}

export function formatAiAttributeValueRenderType(renderType?: AiAttributeValueRenderType | string): string {
    switch (renderType) {
        case "TEXT":
            return "text";
        case "CVE":
            return "CVE";
        case "URL":
            return "URL";
        default:
            return renderType ?? "-";
    }
}

export function describeAiAttributeValueRenderType(renderType?: AiAttributeValueRenderType | string): string {
    switch (renderType) {
        case "TEXT":
            return "Render as plain text tags.";
        case "CVE":
            return "Treat values as CVE IDs and add SEAL search and NVD links.";
        case "URL":
            return "Treat values as URLs and render clickable links.";
        default:
            return "";
    }
}

export function getAiAttributeValueRenderTypeOptions(): { value: AiAttributeValueRenderType; label: string }[] {
    return AI_ATTRIBUTE_VALUE_RENDER_TYPES.map(value => ({
        value,
        label: formatAiAttributeValueRenderType(value)
    }));
}

export const AI_ATTRIBUTE_RATING_OPERATORS = [
    {name: "gt", value: "gt", label: ">"},
    {name: "ge", value: "ge", label: "≥"},
    {name: "eq", value: "eq", label: "="},
    {name: "le", value: "le", label: "≤"},
    {name: "lt", value: "lt", label: "<"},
    {name: "ne", value: "ne", label: "≠"},
];

export const AI_ATTRIBUTE_TEXT_OPERATORS = [
    {name: "textEq", value: "textEq", label: "="},
    {name: "textNe", value: "textNe", label: "≠"},
    {name: "textContains", value: "textContains", label: "contains"},
];

export const AI_ATTRIBUTE_SUMMARY_OPERATORS = [
    {name: "textContains", value: "textContains", label: "contains"},
];

class AiAttribute {
    constructor(
        public id?: string,
        public createdAt?: Date,
        public name?: string,
        public prompt?: string,
        public resultType?: AiAttributeResultType,
        public valueRenderType?: AiAttributeValueRenderType,
        public allowedValues?: string[],
        public interestId?: string,
        public owner?: ResourceOwnerType,
        public organization?: Organization,
        public aiExtractors?: { id: string, name: string }[]
    ) {
    }

    public static fromPOJO(data: any) {
        if (!data) return null;

        const entity = new AiAttribute();

        entity.id = data.id;
        entity.createdAt = data.createdAt ? new Date(data.createdAt) : undefined;
        entity.name = data.name;
        entity.prompt = data.prompt;
        entity.resultType = data.resultType;
        entity.valueRenderType = data.valueRenderType;
        entity.allowedValues = data.allowedValues ?? [];
        entity.interestId = data.interestId;
        entity.owner = data.owner;
        entity.organization = data.organization ? Organization.fromPOJO(data.organization) : undefined;
        entity.aiExtractors = data.aiExtractors ?? [];

        return entity;
    }
}

export default AiAttribute;
